import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { formatINR } from '@/lib/format';
import { firstContact, lastUpdate, type Lead } from './leadDomain';

interface Props {
  open: boolean;
  lead: Lead | null;
  onClose: () => void;
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-wide text-[var(--text-muted)]">{label}</div>
      <div className="mt-0.5 text-sm font-semibold text-[var(--text-primary)]">{children}</div>
    </div>
  );
}

/** Read-only detail view of a single lead, including its communication log. */
export function LeadViewModal({ open, lead, onClose }: Props) {
  if (!lead) return null;
  const comms = [...(lead.comms ?? [])].sort((a, b) => b.date.localeCompare(a.date));

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`${lead.customer} — Lead details`}
      size="md"
      footer={<Button variant="glass" onClick={onClose}>Close</Button>}
    >
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
        <Field label="Inquiry Date">{lead.date}</Field>
        <Field label="Category"><Badge tone="accent">{lead.category}</Badge></Field>
        <Field label="Status"><Badge tone="info">{lead.status}</Badge></Field>
        <Field label="Phone / WhatsApp">{lead.contactNumber || '—'}</Field>
        <Field label="Email">{lead.email || '—'}</Field>
        <Field label="Source">
          {lead.source}
          {lead.source === 'Referral' && lead.referralName ? ` · ${lead.referralName}` : ''}
        </Field>
        <Field label="Assigned To">{lead.assignedTo || '—'}</Field>
        <Field label="Sampling Cost">{lead.samplingCostInr ? formatINR(lead.samplingCostInr) : '—'}</Field>
        <Field label="Order Value">{lead.valueInr ? formatINR(lead.valueInr) : '—'}</Field>
      </div>

      {lead.notes && (
        <div className="mt-4">
          <div className="text-[10px] font-bold uppercase tracking-wide text-[var(--text-muted)]">Notes</div>
          <div className="mt-1 whitespace-pre-wrap rounded-lg border border-[var(--border-color)] bg-[var(--bg-soft)] px-3 py-2 text-sm text-[var(--text-secondary)]">
            {lead.notes}
          </div>
        </div>
      )}

      <div className="mt-4">
        <div className="mb-2 flex flex-wrap gap-4 text-xs text-[var(--text-muted)]">
          <span>First contact: <strong className="text-[var(--text-primary)]">{firstContact(lead)}</strong></span>
          <span>Last update: <strong className="text-[var(--text-primary)]">{lastUpdate(lead)}</strong></span>
          <span>Logs: <strong className="text-[var(--text-primary)]">{comms.length}</strong></span>
        </div>
        <div className="max-h-56 overflow-y-auto rounded-lg border border-[var(--border-color)]">
          {comms.length === 0 ? (
            <div className="px-4 py-6 text-center text-sm text-[var(--text-muted)]">No communication logged yet.</div>
          ) : (
            comms.map((c, i) => (
              <div key={c.id} className="px-4 py-2.5" style={{ borderBottom: i < comms.length - 1 ? '1px solid var(--border-color)' : 'none' }}>
                <div className="mb-1 flex items-center gap-2 text-[11px] text-[var(--text-muted)]">
                  <Badge tone="info">{c.type}</Badge>
                  <span>{c.date}{c.by ? ` · ${c.by}` : ''}</span>
                </div>
                {c.note && <div className="text-sm text-[var(--text-secondary)]">{c.note}</div>}
              </div>
            ))
          )}
        </div>
      </div>
    </Modal>
  );
}
